import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import Column from "@amzn/meridian/esm/components/column";
import { AIMS_NOT_AVAILABLE_MESSAGE, AIMS_STATUS, TABS, ROLES, OUTLOOK_WARNINGS, SUMMARIZE_STATES, TRANSCRIPTION_STATES, RTMS_REQUESTER_DEFAULT_NAME, SESSION_STORAGE_KEYS, AIMS_AVAILABILITY_MESSAGE_TYPES, TRANSCRIPT_NOT_AVAILABLE_MESSAGE, AUTO_START_MODES, AVAILABILITY_TIMEOUT_ERROR } from "../../constants";
import AimsMasthead from "./AimsMasthead";
import Summaries from "./inMeeting/summary/Summaries";
import SummarizeBox from "./inMeeting/organizer/SummarizeBox";
import Help from "./inMeeting/Help";
import Settings from "../settings/Settings";
import VerbosityDiscoveryBanner from "./VerbosityDiscoveryBanner";
import Loading from "../../components/Loading";
import AttendeeHomeView from "./inMeeting/attendee/AttendeeHomeView";
import AimsNotAvailable from "./inMeeting/organizer/AimsNotAvailable";
import AimsException from "../../components/AimsException";
import AlertModal, { ALERT_VARIANT } from "../../components/alertModal/AlertModal";
import Transcript from "../transcript/Transcript";
import LogManager from "../../utils/logManager";
import { fetchAimsAvailabilityApi } from "../../utils/aimsAvailabilityUtils";
import { AimsAvailabilityApiResponse } from "../../localtest/LocalhostTest";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../../utils/rtmsSessionStatusUtils";
import { usePreference } from "../../context/PreferenceContext";

const SHAREDEV_HOSTNAME = "sharedevaimsui.test.aims.meetex.enterprise-engineering.aws.dev";

/**
 * This is the main entry point for the AiMS Zoom App.
 *
 * - It initializes the Zoom SDK and, based on the running context (where the app is running)
 *   and user role (host or attendee), renders the appropriate view.
 * - If the user is the meeting organizer and is in a meeting, it calls the `/is-aims-available` API
 *   to determine the meeting type.
 *   The meeting type impacts:
 *     1) Whether the host can see the "Summarize" button (whether AiMS can be enabled)
 *     2) Who will receive the generated meeting summary
 */
const LegacyHome = ({ setLastKnownContext, featureFlags }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loadPreferences } = usePreference();
  const defaultTab = location.state?.selectedTab || TABS.MAIN;

  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState(null);

  //TODO: change AIMS status to null as default status once the bug fixed: https://sim.amazon.com/issues/MEETEX-169856
  const [aimsStatus, setAimsStatus] = useState(AIMS_STATUS.AVAILABLE);
  
  // Separate state management for summarization and transcription
  const [summarizeState, setSummarizeState] = useState(SUMMARIZE_STATES.DEFAULT);
  const [transcriptionState, setTranscriptionState] = useState(TRANSCRIPTION_STATES.DEFAULT);
  const [isRestarting, setIsRestarting] = useState(false);

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [runningContext, setRunningContext] = useState("inMeeting");
  const [userContext, setUserContext] = useState({});
  const [meetingUUID, setMeetingUUID] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [aimsNotAvailableMessage, setAimsNotAvailableMessage] = useState(AIMS_NOT_AVAILABLE_MESSAGE.DEFAULT);
  const [attendeeSessionStatus, setAttendeeSessionStatus] = useState(null);
  const [alias, setAlias] = useState(null);
  const [appContext, setAppContext] = useState(null);
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(false);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(null);
  const [hasOutlookWarning, setHasOutlookWarning] = useState(false);
  const [rtmsStreamId, setRtmsStreamId] = useState(null);
  const [hasGermanyAttendee, setHasGermanyAttendee] = useState(false);
  const [autoStartMode, setAutoStartMode] = useState(null);
  const [isAutostarting, setIsAutostarting] = useState(false);

  // Reference to track current summarizeState value for setTimeout callbacks (persists across tab switching)
  const summarizeStateRef = useRef(summarizeState);
  const isRtmsStartedByOtherRef = useRef(isRtmsStartedByOther);

  // Keep ref synchronized with current state
  useEffect(() => {
    summarizeStateRef.current = summarizeState;
  }, [summarizeState]);

  useEffect(() => {
    isRtmsStartedByOtherRef.current = isRtmsStartedByOther;
  }, [isRtmsStartedByOther]);

  /**
   * Process AIMS availability response and update component state accordingly.
   * @param {object} aimsAvailability - Parsed JSON object from AIMS availability API.
   */
  const handleAimsAvailabilityResult = (aimsAvailability) => {
    if (!aimsAvailability) {
      LogManager.warn("The aimsAvailability value is null, show exception view.");
      setAimsStatus(AIMS_STATUS.EXCEPTION);
      return;
    }

    const {
      result: AimsAvailableResult,
      message: AimsAvailableMessage,
      hasAttorneyParticipant,
      warningFromZoom,
      warningFromOutlook,
      hasExternalUser,
      role,
      alias,
      hasGermanyAttendee,
      enableAutoStart,
      autoStartMode,
    } = aimsAvailability;

    // Set user role information
    setRole(role);
    setAlias(alias);
    const hasOutlookWarning = Object.values(OUTLOOK_WARNINGS).includes(warningFromOutlook);
    setHasOutlookWarning(hasOutlookWarning);
    setHasGermanyAttendee(hasGermanyAttendee || false);
    LogManager.info(`User alias: ${alias}, role: ${role}`);

    if (AimsAvailableResult === AIMS_STATUS.TRANSCRIPT_AVAILABLE) {
      LogManager.info("Transcript is available for this meeting.");
      setAimsStatus(AIMS_STATUS.TRANSCRIPT_AVAILABLE);
      setAutoStartMode(autoStartMode);
      LogManager.info(`Auto start mode: ${autoStartMode}`);
    } else if (AimsAvailableResult === AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE) {
      LogManager.info("Transcript is not available for this meeting.");
      setAimsStatus(AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE);
    } else if (AimsAvailableResult === AIMS_STATUS.AVAILABLE) {
      if (hasOutlookWarning) {
        LogManager.info("This meeting has MultiMeetingFoundFromOutlook/MeetingNotFoundFromOutlook warning.");
        setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      } else {
        LogManager.info("This is low risk meeting.");
        setAimsStatus(AIMS_STATUS.AVAILABLE);
      }
    } else if (AimsAvailableResult === AIMS_STATUS.NOT_AVAILABLE) {
      LogManager.info("This is AimsNotAvailable meeting.");
      setAimsStatus(AIMS_STATUS.NOT_AVAILABLE);
      if (AimsAvailableMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.WORK_COUNCIL) {
        setAimsNotAvailableMessage(AIMS_NOT_AVAILABLE_MESSAGE.REGIONS);
      } else if (AimsAvailableMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.PERFORMANCE_REVIEW) {
        setAimsNotAvailableMessage(AIMS_NOT_AVAILABLE_MESSAGE.PERFORMANCE)
      }
    } else {
      LogManager.warn("Unknown AimsAvailableResult value:", AimsAvailableResult);
      setAimsStatus(AIMS_STATUS.EXCEPTION);
    }
  };

  /**
   * Initialize Zoom SDK, fetch meeting and user context and check AIMS availability.
   */
  const initializeZoomSdk = async () => {
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Skipping zoomSdk.config() in localhost");
      return;
    }
    // Track whether Home_PageView was already emitted so the catch fallback doesn't double-count.
    // The inMeeting success emit is followed by more code (availability/RTMS calls) that can throw
    // and land in catch, so a plain fallback emit would count the same page view twice.
    let homePageViewEmitted = false;
    try {
      const configResponse = await zoomSdk.config({
        popoutSize: { width: 320, height: 760 },
        capabilities: [
          "getAppContext",
          "getMeetingContext",
          "getRunningContext",
          "getUserContext",
          "openUrl",
          "getMeetingUUID",
          "authorize",
          "onAuthorized",
          "sendMessageToChat",
          "onRTMSStatusChange",
          "getRTMSStatus",
        ],
      });
      LogManager.info("Zoom SDK Configured Successfully:", configResponse);

      const runningContext = await zoomSdk.getRunningContext();
      LogManager.info("The runningContext is:", runningContext);

      console.log("Document Visibility State:", document.visibilityState);
      setRunningContext(runningContext.context);
      setLastKnownContext(runningContext.context);

      if (runningContext.context === "inMainClient") {
        //TODO: Logging - find a identifier in inMainClient for the user
        LogManager.info("The app is running in main client now, show MainClientHome view.");
        // role is not available outside a meeting; backend fills unknown for it.
        LogManager.metric("Home_PageView", 1, { context: runningContext.context });
        homePageViewEmitted = true;
        return;
      }

      //The runningContext is inMeeting
      const meetingUUID = (await zoomSdk.getMeetingUUID()).meetingUUID;
      console.log("The meetingUUID is:" + meetingUUID);
      setMeetingUUID(meetingUUID);

      const userContext = await zoomSdk.getUserContext();
      LogManager.info("The userContext is:", userContext);
      setUserContext(userContext);

      // Emit the page-view metric now that context and role are resolved. Emitting it at the top
      // of useEffect (before config resolves) races the SDK and lands with empty dimensions.
      LogManager.metric("Home_PageView", 1, {
        context: runningContext.context,
        ...(userContext.role ? { userRole: userContext.role } : {}),
      });
      homePageViewEmitted = true;

      let aimsAvailability = null;

      //Call aimsAvailability api first to get the alias
      if (["host", "coHost"].includes(userContext.role)) {
        LogManager.info("The app is running inMeeting and user is host or coHost.");

        //Only host and coHost can call this api
        const meetingContext = await zoomSdk.getMeetingContext();
        LogManager.info("The meetingContext is: ", meetingContext);
        setMeetingId(meetingContext.meetingID);

        const [availabilityResult] = await Promise.all([
          fetchAimsAvailabilityApi(
            meetingUUID,
            meetingContext.meetingID,
            meetingContext.meetingTopic,
            userContext.screenName
          ),
          loadPreferences(),
        ]);
        aimsAvailability = availabilityResult;
        handleAimsAvailabilityResult(aimsAvailability);
        
        // Get app context for secure backend validation (only needed for organizers/alternative hosts)
        // Awaited so appContext is available before auto-start decisions below
        try {
          const appContextResponse = await zoomSdk.getAppContext();
          if (window.location.hostname === SHAREDEV_HOSTNAME) {
            // Temporary AiMS for China POC instrumentation. This intentionally exposes the
            // complete, replayable Zoom app context only in the local ShareDev Web Inspector.
            // Do not replace console with LogManager because that would persist it remotely.
            console.log("[AiMS China POC] getAppContext response:", appContextResponse);
            console.log(
              "[AiMS China POC] getAppContext JSON snapshot:",
              JSON.stringify(appContextResponse)
            );
            console.log(
              "[AiMS China POC] raw app context:",
              appContextResponse?.context
            );
          }
          LogManager.metric("ZoomSDK_GetAppContext_Success", 1);
          // Log only that a context arrived, NEVER its value (Requirement 3.9). The
          // Zoom app context is an encrypted bearer credential: presented to the edge
          // inside its 5-minute freshness window it mints a HIRE token, and it
          // carries no nonce, so a logged copy is replayable by anyone who can read
          // the log group. LogManager forwards to /log, so stringifying the response
          // published the live value to CloudWatch on every in-meeting load.
          //
          // This is the canonical note; the other context call sites point here.
          LogManager.info(
            "app context received: " +
              (appContextResponse?.context ? "present" : "absent")
          );

          if (!appContextResponse || !appContextResponse.context) {
            LogManager.error("getAppContext() returned invalid response - cannot proceed with RTMS operations");
            setAimsStatus(AIMS_STATUS.EXCEPTION);
          } else {
            setAppContext(appContextResponse.context);
          }
        } catch (err) {
          LogManager.error("Failed to get app context from Zoom SDK:", err);
          LogManager.metric("ZoomSDK_GetAppContext_Failure", 1);
          setAimsStatus(AIMS_STATUS.EXCEPTION);
        }
        
        LogManager.info("Finish the initialization for host or coHost.");

      } else {
        LogManager.info("The app is running inMeeting and user is attendee.");

        // Set role to attendee directly without calling availability API
        setRole(ROLES.ATTENDEE);
        loadPreferences();
      }

      // Check RTMS session status for all user types (after alias is available for organizers/hosts)
      const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
      LogManager.info("RTMS status is", sessionStatus);
      
      // Process session status information for all user types
      if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
        // Set both states when session is active
        setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
        setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
        
        // Save the rtmsStreamId when session is active
        if (sessionStatus.rtmsStreamId) {
          setRtmsStreamId(sessionStatus.rtmsStreamId);
          LogManager.info("Saved rtmsStreamId on initialization:", sessionStatus.rtmsStreamId);
        }

        // Determine who started the session
        if (userContext.role === ROLES.ATTENDEE) {
          // Attendees always see it as started by someone else
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        } else if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE
                   && aimsAvailability.autoStartMode === AUTO_START_MODES.MULTIPLE) {
          // Per-host transcription: never block the transcribe button based on who started RTMS.
          // Each host/alt-host manages their own independent session.
          setIsRtmsStartedByOther(false);
        } else if (!sessionStatus.rtmsRequesterAlias) {
          // No requester info available - session started by someone else
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
        } else if (!aimsAvailability?.alias) {
          // Current user's alias not available - treat as unknown
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        } else if (sessionStatus.rtmsRequesterAlias !== aimsAvailability.alias) {
          // Started by someone else
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        } else {
          // Started by current user (organizer/host only)
          setIsRtmsStartedByOther(false);
        }
      } else {
        LogManager.info("No RTMS session found");
        setSummarizeState(SUMMARIZE_STATES.DEFAULT);
        setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
        
        // Single-stream auto-start: only when no session exists
        if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE &&
            aimsAvailability.enableAutoStart &&
            aimsAvailability.autoStartMode === AUTO_START_MODES.SINGLE) {
          LogManager.info("Autostart conditions met: transcript available, no existing session, enableAutoStart is true and autoStartMode is single");
          setIsAutostarting(true);
        }
      }
        
      // Per-host transcription auto-start: trigger even if someone else has an active session.
      // Each host/alt-host manages their own independent session, so we always auto-start
      // regardless of meeting-level session status.
      if (aimsAvailability && aimsAvailability.result === AIMS_STATUS.TRANSCRIPT_AVAILABLE &&
          aimsAvailability.enableAutoStart &&
          aimsAvailability.autoStartMode === AUTO_START_MODES.MULTIPLE) {
        LogManager.info("Per-host transcription autostart: transcript available, enableAutoStart is true and autoStartMode is multiple");
        setIsAutostarting(true);
      }

      // Store session status for attendee (for AttendeeHomeView)
      if (userContext.role === ROLES.ATTENDEE) {
        setAttendeeSessionStatus(sessionStatus.status);
      }
    } catch (error) {
      LogManager.error("Error fetching context or call AimsAvailability/RTMS Status API:", error);
      // Don't lose the page view if an SDK/API call threw before it was emitted; backend fills
      // unknown dimensions. Guarded so we don't double-count when the throw happens after the
      // success emit above (e.g. availability/RTMS calls failing).
      if (!homePageViewEmitted) {
        LogManager.metric("Home_PageView", 1);
      }
      setAimsStatus(error.message === AVAILABILITY_TIMEOUT_ERROR ? AIMS_STATUS.TIMEOUT : AIMS_STATUS.EXCEPTION);
    } finally {
      setIsLoading(false);
    }
  };

  // Main effect: configure SDK and fetch AIMS status
  useEffect(() => {
    // Home_PageView is emitted inside initializeZoomSdk once context/role resolve, so the
    // metric carries context/userRole dimensions instead of racing the SDK config.
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Running in localhost mode.");
      handleAimsAvailabilityResult(AimsAvailabilityApiResponse);
      setRole(ROLES.ORGANIZER);
      //Running context value: inMeeting, inMainClient
      setRunningContext("inMeeting");
      setMeetingUUID("testUUID");
      setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      setIsLoading(false);
    } else {
      initializeZoomSdk();
      // Clear summaries cache on app initialization (refresh)
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA);
      LogManager.info("Cleared summaries cache on app initialization");
    }
  }, []);

  // redirect to /client when inMainClient is detected
  useEffect(() => {
    if (runningContext === "inMainClient") {
      LogManager.info("Detected inMainClient context, redirecting to /client.");
      navigate("/client");
    }
  }, [runningContext, navigate]);

  // Don't show any UI until context detection is complete
  // This prevents the flash of Home.jsx content before redirect
  if (isLoading || runningContext === "inMainClient") {
    return <Loading />;
  }

  // Check if this is a transcript-only scenario (no tabs needed)
  const isTranscriptMode = aimsStatus === AIMS_STATUS.TRANSCRIPT_AVAILABLE || aimsStatus === AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE;

  // If transcript mode, render the Transcript component without tabs
  if (isTranscriptMode) {
    return (
      <Transcript
        aimsStatus={aimsStatus}
        role={role}
        meetingId={meetingId}
        meetingUuid={meetingUUID}
        alias={alias}
        appContext={appContext}
        initialIsRtmsStartedByOther={isRtmsStartedByOther}
        initialRtmsRequesterName={rtmsRequesterName}
        transcriptionState={transcriptionState}
        setTranscriptionState={setTranscriptionState}
        isRestarting={isRestarting}
        setIsRestarting={setIsRestarting}
        isAutostarting={isAutostarting}
        setIsAutostarting={setIsAutostarting}
        autoStartMode={autoStartMode}
      />
    );
  }

  return (
    <Column spacing="none">
      <AimsMasthead selectedTab={selectedTab} setSelectedTab={setSelectedTab} isVerboseSummariesEnabled={featureFlags?.isVerboseSummariesEnabled} />
      {selectedTab === TABS.SUMMARIES ? (
        <Summaries isAttendanceReportEnabled={featureFlags?.isAttendanceReportEnabled} />
      ) : selectedTab === TABS.SETTINGS ? (
        <Settings isVerboseSummariesEnabled={featureFlags?.isVerboseSummariesEnabled} />
      ) : selectedTab === TABS.HELP ? (
        <Help />
      ) : selectedTab === TABS.MAIN ? (
        isLoading ? (
          <Loading />
        ) : role === ROLES.ATTENDEE || (role === ROLES.ALTERNATIVE_HOST && aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) ? (
            <AttendeeHomeView 
              meetingUUID={meetingUUID} 
              aimsStatus={aimsStatus}
              role={role}
              initialSessionStatus={attendeeSessionStatus}
              initialRtmsRequesterName={rtmsRequesterName}
              isAttendanceReportEnabled={featureFlags?.isAttendanceReportEnabled}
            />
          ) : aimsStatus === AIMS_STATUS.TIMEOUT ? (
            <AlertModal variant={ALERT_VARIANT.AIMS_TIMEOUT} isOpen={true} />
          ) : aimsStatus === AIMS_STATUS.EXCEPTION ? (
            <AimsException />
          ) : aimsStatus === AIMS_STATUS.NOT_AVAILABLE ? (
            <AimsNotAvailable aimsNotAvailableMessage={aimsNotAvailableMessage} />
          ) : (role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST) ? (
            <SummarizeBox
              meetingId={meetingId}
              meetingUuid={meetingUUID}
              aimsStatus={aimsStatus}
              role={role}
              alias={alias}
              appContext={appContext}
              hasOutlookWarning={hasOutlookWarning}
              hasGermanyAttendee={hasGermanyAttendee}
              summarizeState={summarizeState}
              setSummarizeState={setSummarizeState}
              summarizeStateRef={summarizeStateRef}
              isRestarting={isRestarting}
              setIsRestarting={setIsRestarting}
              isRtmsStartedByOther={isRtmsStartedByOther}
              rtmsRequesterName={rtmsRequesterName}
              rtmsStreamId={rtmsStreamId}
              setIsRtmsStartedByOther={setIsRtmsStartedByOther}
              setRtmsRequesterName={setRtmsRequesterName}
              setRtmsStreamId={setRtmsStreamId}
              isRtmsStartedByOtherRef={isRtmsStartedByOtherRef}
              isAttendanceReportEnabled={featureFlags?.isAttendanceReportEnabled}
            />
          ) : null
      ) : null}
      {featureFlags?.isVerboseSummariesEnabled && selectedTab === TABS.MAIN && summarizeState === SUMMARIZE_STATES.SUMMARIZING && (
        <div style={{ position: "fixed", bottom: "16px", left: "0", right: "0" }}>
          <VerbosityDiscoveryBanner onSettingsClick={() => setSelectedTab(TABS.SETTINGS)} />
        </div>
      )}
    </Column>
  );
};

export default LegacyHome;
